import {
  Button,
  Divider,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import { fetchProductData } from '../../../api/products';
import { ProductProviderOptions } from '../../../staticData/tableHead';
import CustomButton from '../../elements/CustomButton';
import Dropdown2 from '../../elements/Dropdown2';
import InputField from '../../elements/InputField';

const ProductProviderForm = ({ onSubmit, product, setError, token, mode, provider }) => {
  const [loading, setLoading] = useState(false);
  const [scrapedData, setScrapedData] = useState(false);
  const [isOtherProvider, setIsOtherProvider] = useState(false);
  const [show, setShow] = useState(false);

  const providers =
    product?.providers?.map((provider) => provider.provider_name) || [];

  const availableTypes = ProductProviderOptions.filter(
    (option) => !providers.includes(option.value)
  );

  const handleFetch = async (values, setValues) => {
    setError(null);
    try {
      const payload = {
        url: values.url,
        providerName: values.provider,
        productId: product.id,
      };
      setLoading(true);
      const response = await fetchProductData(payload, token);
      if (!response.ok) {
        setLoading(false);
        const errorResponse = await response.json();
        throw new Error(errorResponse.error?.message || errorResponse.message);
      }
      const data = await response.json();
      setScrapedData(true);
      setValues({
        ...values,
        url: data.url,
        price: data.price,
        rating: data.rating || 0,
        reviewCount: data.review_count || 0,
      });
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch data');
      if (error.message.includes('Please provide a valid URL')) {
        setShow(false);
      } else {
        setShow(true);
      }
    }
  };

  const validationSchema = Yup.object().shape({
    provider: Yup.string().required('Store is required'),
    otherProvider: isOtherProvider
      ? Yup.string().required('Store name is required')
      : Yup.string().nullable(),
    url: Yup.string().url('Invalid URL format').required('URL is required'),
    price: Yup.string()
      .matches(/^\$?[\d,]+(\.\d{1,2})?$/, 'Invalid price format')
      .required('Product Price is required'),
    reviewUrl: Yup.string().url('Invalid URL format'),
    rating: Yup.string()
      .test(
        'rating-required',
        'Rating is required when Review URL is provided',
        function (value) {
          const { reviewUrl } = this.parent;
          if (reviewUrl && reviewUrl.trim() !== '' && !value) {
            return false;
          }
          return true;
        }
      )
      .test('rating-range', 'Rating must be between 0 and 5', function (value) {
        return !value || (parseFloat(value) >= 0 && parseFloat(value) <= 5);
      }),
    reviewCount: Yup.string().test(
      'reviewCount-required',
      'Review count is required when Review URL is provided',
      function (value) {
        const { reviewUrl } = this.parent;
        if (reviewUrl && reviewUrl.trim() !== '' && !value) {
          return false;
        }
        return true;
      }
    ),
  });

  return (
    <Formik
      initialValues={{
        provider: provider?.provider_name || '',
        url: provider?.url || '',
        price: provider?.price || '',
        reviewCount: provider?.review_count || '',
        rating: provider?.rating || '',
        otherProvider: '',
        reviewUrl: provider?.review_url || '',
        icon: provider?.icon || '',
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({
        errors,
        handleBlur,
        handleSubmit,
        touched,
        values,
        handleChange,
        setValues,
        setFieldValue,
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          {availableTypes.length > 0 ? (
            <Grid container spacing={2}>
              {mode !== 'edit' &&               
              <Grid item xs={12} md={4}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="provider"
                    selected={values.provider}
                    handleChange={(e) => {
                      handleChange(e);
                      if (e.target.value === 'other') {
                        setIsOtherProvider(true);
                      } else {
                        setIsOtherProvider(false);
                      }
                    }}
                    elementLabel="Store"
                    options={availableTypes}
                    required
                    disabled={scrapedData}
                  />
                </Stack>
              </Grid>
              }
              {isOtherProvider && (
                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    <InputField
                      label="Store name"
                      id="otherProvider"
                      type="text"
                      value={values.otherProvider}
                      name="otherProvider"
                      fullWidth
                      placeholder="Enter store name"
                      error={Boolean(
                        touched.otherProvider && errors.otherProvider
                      )}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      required
                    />
                  </Stack>
                </Grid>
              )}
              {mode !== 'edit' && values.provider && (
                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    <InputLabel>
                      Store icon{' '}
                      <Typography variant="caption">(optional)</Typography>
                    </InputLabel>
                    <Grid
                      item
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: values.icon ? 'space-evenly' : 'center',
                      }}>
                      <div style={{ marginRight: 10 }}>
                        {values.icon && typeof values.icon === 'string' ? (
                          <img
                            alt="Selected File"
                            src={values.icon}
                            style={{ width: 50, height: 50 }}
                          />
                        ) : values.icon && typeof values.icon !== 'string' ? (
                          <img
                            alt="Selected File"
                            src={URL.createObjectURL(values.icon)}
                            style={{ width: 50, height: 50 }}
                          />
                        ) : null}
                      </div>
                      <div>
                        <input
                          accept="image/png, image/jpeg, image/jpg"
                          id="icon"
                          name="icon"
                          type="file"
                          onChange={(event) => {
                            const selectedFile = event.currentTarget.files[0];
                            setFieldValue('icon', selectedFile);
                          }}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="icon">
                          <Button
                            variant="outlined"
                            size="small"
                            component="span">
                            {values.icon ? 'Change Icon' : 'Upload Icon'}
                          </Button>
                        </label>
                      </div>
                    </Grid>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputField
                    label="Buy link"
                    id="url"
                    type="text"
                    value={values.url}
                    name="url"
                    fullWidth
                    placeholder="Enter product link"
                    error={Boolean(touched.url && errors.url)}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    disabled={scrapedData || mode === 'edit'}
                  />
                </Stack>
              </Grid>
              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
              {scrapedData || isOtherProvider || show || mode === 'edit' ? (
                <>
                  <Grid item xs={12} md={4}>
                    <InputField
                      label="Price"
                      id="price"
                      type="text"
                      value={`$${values.price}`}
                      name="price"
                      fullWidth
                      error={Boolean(touched.url && errors.url)}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        const formattedPrice = e.target.value.replace('$', '');
                        handleChange({
                          target: {
                            name: 'price',
                            value: formattedPrice,
                          },
                        });
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider
                      sx={{ my: 2, backgroundColor: 'rgba(0, 0, 0, 1)' }}
                    />
                    <Stack spacing={1}>
                      <Typography variant="h6">
                        Review link{'  '}
                        <Typography variant="caption">
                          (Other reviews across the web)
                        </Typography>
                      </Typography>
                      <InputField
                        // label="Review link"
                        id="reviewUrl"
                        type="text"
                        value={values.reviewUrl}
                        name="reviewUrl"
                        fullWidth
                        placeholder="Enter review link"
                        onChange={handleChange}
                        error={Boolean(touched.reviewUrl && errors.reviewUrl)}
                        onBlur={handleBlur}
                      />
                    </Stack>
                    {/* *without review link this store will not be visible in
                      other reviews section */}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputField
                      label="Rating (out of 5)"
                      id="rating"
                      type="text"
                      value={values.rating}
                      name="rating"
                      fullWidth
                      onChange={handleChange}
                      error={Boolean(touched.rating && errors.rating)}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InputField
                      label="Review count"
                      id="reviewCount"
                      type="text"
                      value={values.reviewCount}
                      name="reviewCount"
                      fullWidth
                      onChange={handleChange}
                      error={Boolean(touched.reviewCount && errors.reviewCount)}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomButton buttonLabel={'Add'} />
                  </Grid>
                </>
              ) : (
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={() => handleFetch(values, setValues)}
                    disabled={loading}>
                    {loading ? 'Fetching Data...' : 'Fetch Data'}
                  </Button>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography
              sx={{ textAlign: 'center' }}
              variant="caption"
              component="p">
              No providers available
            </Typography>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default ProductProviderForm;
